import {
  ApprovalWorkflowItemDto,
  AWAssetDto,
  AWStageDto,
  AWStageMemberDto,
  PictureDto,
  UpdateAWDto,
  UpdateAWStageDto,
  UpdateAWStageMemberDto
} from '@api/Api';
import { nonNull } from '@helpers/non-null';

type MemberInitValues = {
  email: string;
  name?: string | null;
  picture?: PictureDto | null;
  pictureUrl?: string | null;
  me?: boolean | null;
  hasDominatingDecision?: boolean;
  canManageAW?: boolean;
  canComment?: boolean;
  canSubmitDecision?: boolean;
  organizationRole?: AWStageMemberDto['organizationRole'];
  workspaceRole?: AWStageMemberDto['workspaceRole'];
};

type StageInitValues = {
  member?: MemberInitValues;
};

type AWSettingsInitValues = {
  lockDecisions?: boolean | null;
  disableNonApprovedDownloads?: boolean | null;
  newVersionBehavior?: ApprovalWorkflowItemDto['newVersionBehavior'] | null;
  password?: string | null;
  customMessage?: string | null;
  allowAllVersions?: boolean | null;
  allowAllComments?: boolean | null;
};

export function getAWInitialSettings(init: AWSettingsInitValues) {
  return {
    lockDecisions: init.lockDecisions ?? false,
    disableNonApprovedDownloads: init.disableNonApprovedDownloads ?? false,
    newVersionBehavior: init.newVersionBehavior ?? 'restart',
    password: init.password ?? null,
    customMessage: init.customMessage ?? null,
    allowAllVersions: init.allowAllVersions ?? false,
    allowAllComments: init.allowAllComments ?? true
  };
}

export function getStageInitialState(init: StageInitValues): AWStageDto {
  return {
    id: `${Date.now()}_${Math.random()}`,
    name: 'Name approval routing stage',
    completionCondition: 'all_decisions_submitted',
    isFinished: false,
    status: 'pending',
    members: init.member ? [getMemberInitialState(init.member)] : []
  };
}

export function getMemberInitialState(
  init: MemberInitValues
): AWStageMemberDto {
  return {
    email: init.email,
    name: init.name ?? null,
    picture: { url: init.picture?.url ?? init.pictureUrl ?? null },
    me: init.me || undefined,
    canSubmitDecision: init.canSubmitDecision ?? false,
    canUploadNewVersion: false,
    canManageAW:
      init.canManageAW ??
      (!!init?.organizationRole &&
        init.workspaceRole !== 'workspace://contributor'),
    canComment: init.canComment ?? false,
    canDownload: true,
    hasCommentPriority: false,
    hasDominatingDecision: init.hasDominatingDecision ?? true,
    decisions: [],
    organizationRole: init.organizationRole,
    workspaceRole: init.workspaceRole
  };
}

export function createAWUpdate(
  prev: ApprovalWorkflowItemDto,
  current: ApprovalWorkflowItemDto
): Omit<UpdateAWDto, 'id'> | undefined {
  const update: Omit<UpdateAWDto, 'id'> = {};
  if (prev.lockDecisions !== current.lockDecisions) {
    update.lockDecisions = current.lockDecisions;
  }
  if (
    prev.disableNonApprovedDownloads !== current.disableNonApprovedDownloads
  ) {
    update.disableNonApprovedDownloads = current.disableNonApprovedDownloads;
  }
  if (prev.customMessage !== current.customMessage) {
    update.customMessage = current.customMessage;
  }
  if (prev.password !== current.password) {
    update.password = current.password;
  }
  if (prev.newVersionBehavior !== current.newVersionBehavior) {
    update.newVersionBehavior = current.newVersionBehavior;
  }
  const stagesToAdd = current.stages.filter(
    (stage) => !prev.stages.some((x) => x.id === stage.id)
  );
  if (stagesToAdd.length) {
    update.stages = update.stages ?? {};
    update.stages.add = stagesToAdd;
  }
  const stagesToRemove = prev.stages.filter(
    (stage) => !current.stages.some((x) => x.id === stage.id)
  );
  if (stagesToRemove.length) {
    update.stages = update.stages ?? {};
    update.stages.remove = stagesToRemove.map((x) => x.id);
  }
  for (const stage of current.stages) {
    if (!stagesToAdd.includes(stage)) {
      const prevStage = nonNull(prev.stages.find((x) => x.id === stage.id));
      const stageUpdate = createAWStageUpdate(prevStage, stage);
      if (stageUpdate) {
        update.stages = update.stages ?? {};
        update.stages.update = update.stages.update ?? [];
        update.stages.update.push(stageUpdate);
      }
    }
  }
  return Object.keys(update).length ? update : undefined;
}

export function createAWStageUpdate(
  prev: AWStageDto,
  current: AWStageDto
): UpdateAWStageDto | undefined {
  const update: Omit<UpdateAWStageDto, 'id'> = {};
  if (prev.completionCondition !== current.completionCondition) {
    update.completionCondition = current.completionCondition;
  }
  if (prev.deadline !== current.deadline) {
    update.deadline = current.deadline;
  }
  if (prev.name !== current.name) {
    update.name = current.name;
  }
  const membersToAdd = current.members.filter(
    (member) => !prev.members.some((x) => x.email === member.email)
  );
  if (membersToAdd.length) {
    update.members = update.members ?? {};
    update.members.add = membersToAdd;
  }
  const membersToRemove = prev.members.filter(
    (member) => !current.members.some((x) => x.email === member.email)
  );
  if (membersToRemove.length) {
    update.members = update.members ?? {};
    update.members.remove = membersToRemove.map((x) => x.email);
  }
  for (const member of current.members) {
    if (!membersToAdd.includes(member)) {
      const prevMember = nonNull(
        prev.members.find((x) => x.email === member.email)
      );
      const memberUpdate = createAWStageMemberUpdate(prevMember, member);
      if (memberUpdate) {
        update.members = update.members ?? {};
        update.members.update = update.members.update ?? [];
        update.members.update.push(memberUpdate);
      }
    }
  }
  return Object.keys(update).length ? { ...update, id: prev.id } : undefined;
}

export function createAWStageMemberUpdate(
  prev: AWStageMemberDto,
  current: AWStageMemberDto
): UpdateAWStageMemberDto | undefined {
  const update: Omit<UpdateAWStageMemberDto, 'email'> = {};
  if (prev.canComment !== current.canComment) {
    update.canComment = current.canComment;
  }
  if (prev.canDownload !== current.canDownload) {
    update.canDownload = current.canDownload;
  }
  if (prev.canManageAW !== current.canManageAW) {
    update.canManageAW = current.canManageAW;
  }
  if (prev.canSubmitDecision !== current.canSubmitDecision) {
    update.canSubmitDecision = current.canSubmitDecision;
  }
  if (prev.canUploadNewVersion !== current.canUploadNewVersion) {
    update.canUploadNewVersion = current.canUploadNewVersion;
  }
  if (prev.decisions?.length !== current.decisions?.length) {
    update.decisions = current.decisions;
  }
  if (prev.hasCommentPriority !== current.hasCommentPriority) {
    update.hasCommentPriority = current.hasCommentPriority;
  }
  if (prev.hasDominatingDecision !== current.hasDominatingDecision) {
    update.hasDominatingDecision = current.hasDominatingDecision;
  }
  return Object.keys(update).length
    ? { ...update, email: prev.email }
    : undefined;
}

/**
 * Assets in a previous order (by id), e.g. to keep a decision from moving an
 * asset sorted by decision. Assets not in it (added since) follow, in the
 * order given.
 */
export function keepAssetsOrder<T extends { id: string }>(
  assets: T[],
  order: string[]
): T[] {
  const positions = new Map(order.map((id, index) => [id, index]));
  const getPosition = (item: T) => positions.get(item.id) ?? order.length;
  // Stable sort: assets not in the order keep their relative order
  return [...assets].sort((a, b) => getPosition(a) - getPosition(b));
}

export function sortedAWListAssetsToReviewInBatch(
  data: AWAssetDto[],
  sort = 'needReview'
) {
  if (!data?.length) return [];
  if (sort === 'completedReview') {
    return data.sort((a, b) => {
      if (a.accessDisabled && !b.accessDisabled) return 1;
      if (!a.accessDisabled && b.accessDisabled) return -1;
      if (a.decision && !b.decision) return -1;
      if (!a.decision && b.decision) return 1;

      return new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime();
    });
  }
  return data.sort((a, b) => {
    if (a.accessDisabled && !b.accessDisabled) return 1;
    if (!a.accessDisabled && b.accessDisabled) return -1;
    if (!a.decision && b.decision) return -1;
    if (a.decision && !b.decision) return 1;
    return new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime();
  });
}
